/**
 * useBanner — React Query hook for `ContentReaderPort.getPublishedBanner`
 * (US14 / FR-057). Mirrors `useHomeButtons`: the port is the only read, the
 * locale is forwarded verbatim (no hook-side default, so the cache key is
 * exactly what the caller passed), and the shared `CONTENT_STALE_TIME` keeps
 * the Home and shell mounts on one cached read.
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { Banner } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

export interface UseBannerOptions {
  readonly locale?: string | undefined;
  /** Skip the read entirely (e.g. no session to read as). Defaults to true. */
  readonly enabled?: boolean | undefined;
}

/**
 * Fetch the published banner for the locale. `data` is the `Banner` once
 * resolved, `null` when none is published, `undefined` while pending.
 * `MemberBanner` renders nothing while pending, on error, and for `null`
 * (P7 — absent, not a placeholder).
 */
export function useBanner(options: UseBannerOptions = {}): UseQueryResult<Banner | null, Error> {
  const { locale, enabled = true } = options;
  const port = useContent();

  return useQuery<Banner | null, Error>({
    queryKey: ['content', 'banner', locale],
    queryFn: () => port.getPublishedBanner({ locale }),
    staleTime: CONTENT_STALE_TIME,
    // The read is a session procedure. An anonymous shell (the login route)
    // would only be refused, so it does not ask.
    enabled,
  });
}
